import apiClient from '@/lib/axios';
import type { ReportType, ReportFilters, ReportFormat } from '../types/report';

const EXCEL_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const PDF_MIME_TYPE = 'application/pdf';

/**
 * Downloads a report from the API in the specified format
 *
 * @param reportType - The type of report to download
 * @param format - Output format: 'xlsx' (Excel) or 'pdf'
 * @param filters - Optional date filters for date-based reports
 * @returns Promise that resolves when download is complete
 */
export const downloadReport = async (
  reportType: ReportType,
  format: ReportFormat = 'xlsx',
  filters?: ReportFilters
): Promise<void> => {
  const params = new URLSearchParams();
  params.append('format', format);
  if (filters?.startDate) {
    params.append('startDate', filters.startDate);
  }
  if (filters?.endDate) {
    params.append('endDate', filters.endDate);
  }

  const queryString = params.toString();
  const url = `/reports/${reportType}${queryString ? `?${queryString}` : ''}`;

  const response = await apiClient.get(url, {
    responseType: 'blob',
  });

  // Determine MIME type and extension based on format
  const mimeType = format === 'pdf' ? PDF_MIME_TYPE : EXCEL_MIME_TYPE;
  const extension = format === 'pdf' ? 'pdf' : 'xlsx';

  // Create blob URL from response
  const blob = new Blob([response.data], { type: mimeType });
  const blobUrl = window.URL.createObjectURL(blob);

  // Create download link
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = `${reportType}-${new Date().toISOString().split('T')[0]}.${extension}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Cleanup blob URL
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
};

/**
 * Opens a report as PDF in a new browser tab
 *
 * @param reportType - The type of report to view
 * @param filters - Optional date filters for date-based reports
 * @returns Promise that resolves when the PDF is opened
 */
export const openReportPdf = async (
  reportType: ReportType,
  filters?: ReportFilters
): Promise<void> => {
  const params = new URLSearchParams();
  params.append('format', 'pdf');
  if (filters?.startDate) {
    params.append('startDate', filters.startDate);
  }
  if (filters?.endDate) {
    params.append('endDate', filters.endDate);
  }

  const queryString = params.toString();
  const url = `/reports/${reportType}${queryString ? `?${queryString}` : ''}`;

  const response = await apiClient.get(url, {
    responseType: 'blob',
  });

  // Create blob URL and open in new tab
  const blob = new Blob([response.data], { type: PDF_MIME_TYPE });
  const blobUrl = window.URL.createObjectURL(blob);
  window.open(blobUrl, '_blank');

  // Cleanup blob URL after a delay (give time for the tab to load)
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
};
